import { Control, Controller, FieldValues, Path, PathValue } from "react-hook-form";
import { SwitchWithLabel, SwitchLabelPosition } from "@/components/ui/customs/StyledSwitch";
import { cn } from "@/lib/utils";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";

interface ControlledSwitchProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  defaultValue?: PathValue<T, Path<T>>;
  label: React.ReactNode;
  /** @default "right" */
  labelPosition?: SwitchLabelPosition;
  disabled?: boolean;
  className?: string;
  switchClassName?: string;
  onCheckedChange?: (checked: boolean) => void;
  /** When the field is not a boolean (e.g. an enum), map form value ↔ switch checked. */
  mapValueToChecked?: (value: PathValue<T, Path<T>> | undefined) => boolean;
  mapCheckedToValue?: (checked: boolean) => PathValue<T, Path<T>>;
}

export const ControlledSwitch = <T extends FieldValues>({
  control,
  name,
  defaultValue,
  label,
  labelPosition = "right",
  disabled,
  className,
  switchClassName,
  onCheckedChange,
  mapValueToChecked,
  mapCheckedToValue,
}: ControlledSwitchProps<T>) => {
  const translateMessage = useTranslateMessage();
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field, fieldState: { error } }) => {
        const checked = mapValueToChecked ? mapValueToChecked(field.value) : !!field.value;
        return (
          <div className={cn("flex flex-col gap-1", className)}>
            <SwitchWithLabel
              id={String(name)}
              checked={checked}
              onCheckedChange={(next) => {
                if (mapCheckedToValue) {
                  field.onChange(mapCheckedToValue(next));
                } else {
                  field.onChange(next);
                }
                onCheckedChange?.(next);
              }}
              label={label}
              labelPosition={labelPosition}
              disabled={disabled}
              switchClassName={switchClassName}
            />
            {error?.message ? (
              <p className="text-sm text-red-500">{translateMessage(error.message)}</p>
            ) : null}
          </div>
        );
      }}
    />
  );
};
